// Zendesk Web Widget API and Zopim for Chat Integration

import eventChannel from './eventChannel'
import store from './store/store'
import * as user from './store/user'
import * as events from './store/events'
import * as cross from './store/cross'
import {EventTypeNames} from './store/enum'
import * as track from './track/track'
import {loadScript} from './utils'

const enabledDepartments = ['Replacement', 'Fit Question', 'Style Advice', 'General Inquiries']

const retry = (predicate, fn, maxTries = 12) => {
  let interval = 50
  let tries = 0

  const loop = () => {
    if (tries >= maxTries) {
      // stop
    } else if (predicate()) {
      fn()
    } else {
      interval *= 2
      tries += 1
      setTimeout(loop, interval)
    }
  }

  loop()
}

const _zendesk = []
const _zopim = []
let zendeskInitialized = false
let zopimInitialized = false

export const pushZendesk = (fn) => (zendeskInitialized ? fn() : _zendesk.push(fn))
export const pushZopim = (fn) => (zopimInitialized ? fn() : _zopim.push(fn))

// This uses the ZenDesk Web Widget API to set current user name and email
// if user is authenticated, as well as set suggestions based on current page url
const identify = () =>
  pushZendesk(() => {
    const state = store.getState()

    if (user.isAuthenticated(state)) {
      const firstName = user.firstName(state)
      const lastName = user.lastName(state)
      const email = user.email(state)
      window.zE('webWidget', 'identify', {
        name: firstName ? `${firstName} ${lastName}` : email.split('@')[0],
        email,
      })
    }
  })

const setHelpSuggestions = () => {
  pushZendesk(() => window.zE('webWidget', 'helpCenter:setSuggestions', {url: true}))
  pushZopim(() =>
    window.$zopim(() => window.$zopim.livechat.departments.filter(...enabledDepartments)),
  )
}

const setVisitorPath = () =>
  pushZopim(() => {
    window.$zopim(() =>
      window.$zopim.livechat.sendVisitorPath({
        url: window.location.href,
        title: window.location.pathname,
      }),
    )
  })

const setupChatStartAndEndTracking = () =>
  pushZopim(() => {
    window.$zopim(() => {
      window.$zopim.livechat.setOnChatStart(track.chatInitialized)
      window.$zopim.livechat.setOnChatEnd(track.chatEnded)
    })
  })

export const getTags = (actionTag) => {
  const state = store.getState()
  const tags = actionTag ? [actionTag] : []

  if (cross.customerType(state) !== 'None') tags.push(`CustomerType: ${cross.customerType(state)}`)

  const eventDate = events.currentDate(state)
  if (typeof eventDate === 'string') tags.push(`EventDate: ${eventDate}`)

  const eventType = events.currentType(state)
  if (typeof eventType === 'number' && typeof EventTypeNames[eventType] === 'string') {
    tags.push(`EventType: ${EventTypeNames[eventType]}`)
  }

  if (typeof events.currentNumMembers(state) === 'number') {
    tags.push(`Renters: ${events.currentNumMembers(state)}`)
    if (events.currentNumMembers(state) >= 6) {
      tags.push('Party Size: Large')
    } else if (events.currentNumMembers(state) >= 3) {
      tags.push('Party Size: Medium')
    } else {
      tags.push('Party Size: Small')
    }
  }

  if (typeof events.currentOwnerRole(state) === 'string') {
    tags.push(`CustomerRole: ${events.currentOwnerRole(state)}`)
  }

  return tags
}

export const sendTags = (actionTag) =>
  pushZopim(() => window.$zopim(() => window.$zopim.livechat.addTags(...getTags(actionTag))))

// Reformatted version of the snippet provided by ZenDesk to load the widget.
// Creates window.zE immediately and window.$zopim eventually...
const loadZendeskSnippet = () => {
  if (!window.zEmbed) {
    const arr = []
    const iframe = document.createElement('iframe')

    window.zEmbed = (...args) => arr.push(args)
    window.zE = window.zE || window.zEmbed
    iframe.src = 'javascript:false' // eslint-disable-line no-script-url
    iframe.title = ''
    iframe.role = 'presentation'
    ;(iframe.frameElement || iframe).style.cssText = 'display: none'
    const scriptEls = document.getElementsByTagName('script')
    const lastScriptEl = scriptEls[scriptEls.length - 1]
    lastScriptEl.parentNode.insertBefore(iframe, lastScriptEl)
    const iframeWin = iframe.contentWindow

    let iframeDoc
    let docDomain
    try {
      iframeDoc = iframeWin.document
    } catch (e) {
      docDomain = document.domain
      iframe.src = `javascript:var d=document.open();d.domain="${docDomain}";void(0);`
      iframeDoc = iframeWin.document
    }
    iframeDoc.open()._l = function onLoadIframe() {
      const iframeScriptEl = this.createElement('script')
      if (docDomain) {
        this.domain = docDomain
      }
      iframeScriptEl.id = 'js-iframe-async'
      iframeScriptEl.src = 'https://assets.zendesk.com/embeddable_framework/main.js'
      this.t = +new Date()
      this.zendeskHost = 'theblacktux.zendesk.com'
      this.zEQueue = arr
      this.body.appendChild(iframeScriptEl)
    }
    iframeDoc.write('<body onload="document._l();">')
    iframeDoc.close()
  }
}

const loadSolvvySnippet = () =>
  loadScript('https://cdn.solvvy.com/deflect/customization/theblacktux/solvvy.js').then(() => {
    // send Segment event when user clicks to open Solvvy widget
    document.addEventListener('solvvy_launched', ({detail}) => {
      // Solvvy provides additional data to indicate if the user clicked on a "link" vs a "widget"
      track.solvvyLaunched(detail.originator)
    })
  })

export const initialize = () => {
  if (window.TBT.disable.indexOf('zendesk') !== -1) {
    return
  }

  loadZendeskSnippet()

  // begin utilizing window.zE and window.$zopim when ready
  retry(
    () => window.zE !== undefined && window.$zopim !== undefined,
    () => {
      let fn

      while ((fn = _zendesk.shift())) fn() // eslint-disable-line no-cond-assign
      zendeskInitialized = true

      while ((fn = _zopim.shift())) fn() // eslint-disable-line no-cond-assign
      zopimInitialized = true
    },
  )

  // start tracking user information
  identify()
  setHelpSuggestions()
  setVisitorPath()
  setupChatStartAndEndTracking()

  // set up listeners
  eventChannel.on(eventChannel.userChanged, identify)
  eventChannel.on(eventChannel.pathnameChanged, setHelpSuggestions)
  eventChannel.on(eventChannel.pathnameChanged, setVisitorPath)

  // initialize Solvvy
  loadSolvvySnippet()
}
